import React from 'react';
import { ChatSession } from '../types';
import './Sidebar.css';

interface SidebarProps {
  sessions: ChatSession[];
  currentSessionId: string | null;
  onSessionSelect: (sessionId: string) => void;
  onNewSession: () => void;
  onDeleteSession: (sessionId: string) => void;
  onRenameSession: (sessionId: string, newTitle: string) => void;
  onHideSidebar: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  sessions,
  currentSessionId,
  onSessionSelect,
  onNewSession,
  onDeleteSession,
  onRenameSession,
  onHideSidebar
}) => {
  const [editingSessionId, setEditingSessionId] = React.useState<string | null>(null);
  const [editingTitle, setEditingTitle] = React.useState('');

  const handleRenameStart = (session: ChatSession) => {
    setEditingSessionId(session.id);
    setEditingTitle(session.title);
  };

  const handleRenameSave = () => {
    if (editingSessionId && editingTitle.trim()) {
      onRenameSession(editingSessionId, editingTitle.trim());
    }
    setEditingSessionId(null);
    setEditingTitle('');
  };

  const handleRenameCancel = () => {
    setEditingSessionId(null);
    setEditingTitle('');
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return '今天';
    } else if (days === 1) {
      return '昨天';
    } else if (days < 7) {
      return `${days}天前`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getSessionPreview = (session: ChatSession) => {
    const lastMessage = session.messages[session.messages.length - 1];
    if (!lastMessage) return '新对话';
    
    const content = lastMessage.content;
    return content.length > 30 ? content.substring(0, 30) + '...' : content;
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <h2>聊天记录</h2>
        <div className="header-actions">
          <button 
            className="btn-new-session"
            onClick={onNewSession}
            title="新建对话"
          >
            <span className="icon">+</span>
          </button>

          <button 
            className="btn-hide"
            onClick={onHideSidebar}
            title="隐藏侧边栏"
          >
            <span className="hide-icon">×</span>
          </button>
        </div>
      </div>

      <div className="sessions-list">
        {sessions.length === 0 ? (
          <div className="empty-sessions">
            <div className="empty-icon">💬</div>
            <p>暂无聊天记录</p>
          </div>
        ) : (
          sessions.map((session) => (
            <div
              key={session.id}
              className={`session-item ${session.id === currentSessionId ? 'active' : ''}`}
              onClick={() => onSessionSelect(session.id)}
            >
              <div className="session-content">
                {editingSessionId === session.id ? (
                  <div className="session-edit">
                    <input
                      type="text"
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleRenameSave();
                        } else if (e.key === 'Escape') {
                          handleRenameCancel();
                        }
                      }}
                      onBlur={handleRenameSave}
                      autoFocus
                      className="session-title-input"
                    />
                  </div>
                ) : (
                  <>
                    <div className="session-title">{session.title}</div>
                    <div className="session-preview">{getSessionPreview(session)}</div>
                    <div className="session-meta">
                      <span className="session-date">{formatDate(session.updatedAt)}</span>
                      <span className="session-count">{session.messages.length}条消息</span>
                    </div>
                  </>
                )}
              </div>
              
              {editingSessionId !== session.id && (
                <div className="session-actions">
                  <button
                    className="btn-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRenameStart(session);
                    }}
                    title="重命名"
                  >
                    ✏️
                  </button>
                  <button
                    className="btn-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteSession(session.id);
                    }}
                    title="删除"
                  >
                    🗑️
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Sidebar; 